import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { isValidPassword } from '../_common/_helpers/validation'
import { profileReset } from '../../store/reducers/profileSlice'
import CustomTextField from '../_common/_form_fields/CustomTextField'
import CustomBackdrop from '../_common/CustomBackdrop'

const ChangePasswordWrapper = (props) => {
  const { handleChangePasswordDialogClose, handleNotificationDialogOpen } = props
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [oldPassword, setOldPassword] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const formControlStyle = {
    background: theme.palette.azureishWhite,
    color: theme.palette.violetsAreBlue,
    borderRadius: '30px',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  }

  const outlinedInputErrorStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
  }

  const loginErrorHelperStyle = {
    color: theme.palette.congoPink,
    paddingLeft: '20px',
  }

  const loginHelperStyle = {
    color: 'white',
    paddingLeft: '20px',
  }

  const submitButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },
  }

  const handleChangeOldPassword = (e) => {
    const value = e.target.value
    setOldPassword(value)
    if (value) {
      setErrors(prev => {return { ...prev, oldPassword: null }})
    }    
  }

  const handleChangePassword = (e) => {
    const value = e.target.value
    setPassword(value)
    if (isValidPassword(value)) {
      setErrors(prev => {return { ...prev, password: null }})
    }    
  }

  const handleChangeConfirmPassword = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (value === password) {
      setErrors(prev => {return { ...prev, confirmPassword: null }})
    }    
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const changePasswordRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsersPasswordUpdate
      
    const request_data = { url, method: 'PUT', data: postData }
    
    const response = await request(request_data)
    
    if (response && response.status_code && response.status_code < 400 && response.data && response.data.status && response.data.status === 'updated') {
      handleChangePasswordDialogClose()

      handleNotificationDialogOpen({
        title: defaultTexts.changePassword,
        text: defaultTexts.passwordChangedText,
      })

      setTimeout(() => {
        dispatch(profileReset())
        navigate('/')        
      }, 1000)      
    } else if (response && response.error && response.error.includes('Invalid old password provided')) {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
          oldPassword: defaultTexts.incorrectOldPassword,
        }
      })
    } else {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
        }
      })
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!oldPassword) {
      _errors.oldPassword = defaultTexts.pleaseFillOut
    }

    if (!password) {
      _errors.password = defaultTexts.pleaseFillOut
    } else if (!isValidPassword(password)) {
      _errors.password = defaultTexts.passwordRequirements
    }

    if (!confirmPassword) {
      _errors.confirmPassword = defaultTexts.pleaseFillOut
    } else if (confirmPassword !== password) {
      _errors.confirmPassword = defaultTexts.passwordDoesNotMatch
    }

    if (Object.keys(_errors).length === 0) {
      const postData = {
        old_password: oldPassword,
        password,
        confirm_password: confirmPassword,
      }

      changePasswordRequest(postData)
    }
    
    if (Object.keys(_errors).length > 0) {
      scrollToTop()
      _errors.generalError = defaultTexts.anErrorOccured
      setErrors(_errors)
    }

    setIsLoading(false)
  }

  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        errors.generalError &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{errors.generalError}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.oldPassword}
          currentValue={oldPassword}
          setCurrentValue={handleChangeOldPassword}
          required={true}
          loginError={errors.oldPassword ? true : false}
          errorText={errors.oldPassword}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.newPassword}
          currentValue={password}
          setCurrentValue={handleChangePassword}
          required={true}
          loginError={errors.password ? true : false}
          loginHelperText={defaultTexts.passwordRequirements}
          loginErrorSx={outlinedInputErrorStyle}
          loginHelperSx={errors.password ? loginErrorHelperStyle : loginHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.confirmNewPassword}
          currentValue={confirmPassword}
          setCurrentValue={handleChangeConfirmPassword}
          required={true}
          loginError={errors.confirmPassword ? true : false}
          errorText={errors.confirmPassword}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        sx={{ marginTop: '24px' }}
      >
        <Button
          variant='text'
          disableRipple
          disableElevation
          sx={submitButtonStyle}
          onClick={handleClickSubmit}
        >
          {defaultTexts.submit}
        </Button>
      </Stack>
    </Box>
  )
}

export default ChangePasswordWrapper